
import { ExternalLinkIcon, SettingsIcon, RefreshCwIcon } from "vue-feather-icons";
import CropMixin from "../../mixins/cropper";
import RestrictionMixin from '../../mixins/restrictionMixin';
import FieldParent from /* webpackPrefetch: true */ /* webpackChunkName: "FieldParent" */ "../ui/form/fields/FieldParent";
import PresetBox from /* webpackChunkName: "PresetBox" */ "./PresetBox";
import { limitString, truncate } from "~/fc/Helpers/StringHelpers";
import ToastManager from "~/fc/ToastManager";
import { parentValidator } from "~/fc/advancedOptionHelper";

export default {
    name: "AdvancedOptions",
    components: {
        ExternalLinkIcon,
        RefreshCwIcon,
        SettingsIcon,
        FieldParent,
        PresetBox,
        Dropdown: () => import(/* webpackChunkName: "Dropdown" */ "~/components/ui/Dropdown"),
        JobBuilderDialog: () => import(/* webpackChunkName: "JobBuilderDialog" */ '~/components/file-io/JobBuilderDialog'),
        IconCode: () => import(/* webpackChunkName: "IconCode" */ '~/components/ui/icon/IconCode')
    },
    mixins: [CropMixin, RestrictionMixin],
    props: {
        inPage: { type: Boolean, default: false },
        uid: { type: String, default: "root" },
        slug: { },
        animation: { type: String, default: "slide" },
        showOptionsOnly: { type: Boolean, default: false },
        open: { type: Boolean, default: false },
        previewTemplate: { type: Boolean, default: false },
        dontShowSmallButton: { type: Boolean, default: false },
        showReset: { type: Boolean, default: false },
        noReset: { type: Boolean, default: false },
    },
    data: () => ({
        active: "computer",
        showAdvancedForm: false,
        showSavePresetBox: false,
        showPresetsList: false,
        custom: true,
        percentage: false,
        setMax: true,
        compressLevel: false,
        showAddMore: false,
        count: 0,
        presetRef: 0,
    }),
    computed: {
        merging() { return this.$store.state.advancedSetting.merging; },
        files () { return this.$store.state.list; },
        advancedOptionsLoading() { return this.$store.state.advancedSetting.advancedOptionsLoading; },
        hasFiles() { return !!this.$store.state.list.length; },
        toastManager() { return new ToastManager(this); },
        info() { return this.$store.state.info; },
        singleFile() { return this.info.operation === "crop" || this.info.operation === "trim" || this.files.length === 1 },
        file() { return this.uid ? this.files.find(file => file.id === this.uid) : null },
        isMobile () { return this.$store.state.store.isMobile },
        enableSmallUploader() {
            return (
                !this.$store.state.smallUploaderDisabled &&
                this.$store.state.list.length &&
                !this.showOptionsOnly
            );
        },
        enableAdvancedTitle() {
            return (
                this.list.length &&
                this.list !== "loading" &&
                (this.$store.state.advancedSetting.merging ||
                    this.$store.state.advancedSetting.sorting ||
                    this.$store.state.advancedSetting.showOnlyRootSettings ||
                    (!this.showOptionsOnly && !this.hasFiles))
            );
        },
        isAOVisible() {
            return (
                this.showAdvancedForm &&
                this.list.length &&
                this.list !== "loading" &&
                (this.merging ||
                    this.$store.state.advancedSetting.showOnlyRootSettings ||
                    this.showOptionsOnly ||
                    (!this.showOptionsOnly && !this.hasFiles))
            );
        },
        list() {
            const list = this.slug &&
                this.$store.state.advancedSetting.perConversionType[this.slug] &&
                this.$store.state.advancedSetting.perConversionType[this.slug].list
                ? this.$store.state.advancedSetting.perConversionType[this.slug].list
                : [];
            if (!Array.isArray(list)) return list;
            return list.filter(group => group.options.filter(field => {
                const enablePreview = this.info.customVariables ? this.info.customVariables.enablePreview : false;
                const selection = this.$store.state.advancedSetting.userData.allSelection &&
                this.$store.state.advancedSetting.userData.allSelection[this.uid]
                ? this.$store.state.advancedSetting.userData.allSelection[this.uid]
                : {}
                if (field.only_multifile) return this.inPage && enablePreview
                return parentValidator(field, selection)
            }).length > 0);
        },
        previewPage() {
            return (
                this.info.active === "PreviewTemplate" ||
                (this.info.customVariables
                    ? this.info.customVariables.enableSort
                    : false)
            );
        },
        enablePreview() {
            return (
                this.info.active === "PreviewTemplate" ||
                (this.info.customVariables
                    ? this.info.customVariables.enablePreview
                    : false)
            );
        },
        allowJobBuilderLauncher () {
            /**
             * We are not allowing code example in 
             * the video preview pages for some 
             * limitations on our systems.
             * https://freeconvert.atlassian.net/browse/FC-6206?focusedCommentId=22361
             */
            if(this.isVideoPreview) return false;
            /**
             * code example is not supported for Google drive, One drive or Dropbox
             */
            if(this.file) return !(this.file.gtoken || this.file.uploadMethod === 'dropbox' || this.file.uploadMethod === 'one_drive')

            let invalidUpload = this.files.find(file => {
                return file.gtoken || file.uploadMethod === 'dropbox' || file.uploadMethod === 'one_drive'
            })
            return !invalidUpload
        },
        isVideoPreview() { return (this.info.active === 'PreviewTemplate' || (!!this.info.customVariables?.enablePreview)) && this.info.type === "video" },
        jobBuilderTooltip() {
            if(this.isVideoPreview) return this.$t('api_not_available')
            if(!this.file) return this.allowJobBuilderLauncher ?  "API" : this.$t('api_launcher_hint')
            if(this.file.uploadMethod === "one_drive" || this.file.gtoken) return this.$t('api_launcher_hint')
            if(this.file.uploadMethod === "dropbox") return this.$t('api_launcher_hint_dropbox')   
            return "API"
        },
        isValidDimension() {
            return this.$store.state.isValidDimension;
        },
    },
    watch: {
        merging(val) {
            if (val) this.showAdvancedForm = true;
            this.hideGroupWithNoOptions()
        },
        showAdvancedForm(val) {
            this.$store.commit("showAdvancedForm", val);
        },
        isAOVisible(val) {
            this.$emit("isAOVisibleChange", val);
        }
    },
    created() {
        this.showAdvancedForm =
            this.$store.state.info.advanced_option === "open" ||
            this.showOptionsOnly ||
            this.open;
    },
    mounted() {
        if (!this.dontShowSmallButton) this.$store.commit("enableSmallUploader");
        this.$nextTick(() => { this.handleInitialRender() });
        this.$bus.$on("LOGOUT", () => { this.presetRef++; });
        this.$bus.$on("RESET_AO", () => { this.resetSelection() });
    },
    beforeUnmount() {
        !this.showOptionsOnly && this.$store.commit("disableSmallUploader");
    },
    methods: {
        handleInitialRender() {
            this.handleOptionResponsiveness()
            window.addEventListener("resize", () => {
                this.handleOptionResponsiveness()
            });
            this.hideGroupWithNoOptions()
        },
        handleOptionResponsiveness() {
            if (!(document.getElementById("OptionsList") && this.enablePreview)) return
            const optionsListWidth = document.getElementById("OptionsList")
                ? document.getElementById("OptionsList").offsetWidth
                : 0;
            this.$store.commit(
                "store/updateAdvancedOptionListResponsiveView",
                optionsListWidth <= 500
            );
        },
        resetSelection() {
            this.$bus.$emit("resetSelection");
            if (this.info.operation === "crop") {
                this.$bus.$emit("reset_crop");
            } else {
                this.$store.commit("advancedSetting/resetSelection", this.uid);
            }
            this.$store.commit("setGpuUpgradeWarning", false);
        },
        openSavePresetBox() {
            this.showPresetsList = false;
            this.showSavePresetBox = true;
            this.scrollToPreset();
        },
        openPresetsList() {
            this.showSavePresetBox = false;
            this.showPresetsList = true;
            this.scrollToPreset();
        },
        scrollToPreset() {
            setTimeout(() => {
                const presetBox = document.getElementById("preset-box");
                if (presetBox) {
                    presetBox.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest",
                    });
                }
            }, 200);
        },
        hidePresetBoxes() {
            this.showSavePresetBox = false;
            this.showPresetsList = false;
        },
        prevent(e) {
            e.preventDefault();
        },
        apply(showAlert = true) {
            if (this.hasFiles) {
                if (showAlert)
                    this.toastManager.add(
                        "success",
                        "Applied!",
                        this.$t("settings_applied")
                    );
            }
        },
        applyToAll() {
            if (!this.hasFiles) return;
            this.apply(false);
            const { allSelection } = this.$store.state.advancedSetting.userData;

            Object.keys(allSelection).forEach((id) => {
                if (this.uid !== id && id !== "root") {
                    this.$store.commit("advancedSetting/copySelection", {
                        from: this.uid,
                        to: id,
                    });
                }
            });

            this.toastManager.add(
                "success",
                "Applied!",
                this.$t("settings_applied_all")
            );
        },
        limitString(s, n) {
            return limitString(s, n)
        },
        hideGroupWithNoOptions() {
            /**
             * We need to check if the advanced options array
             * (this.list) is populated and if it's in fact an
             * array before we loop through in order to find
             * and hide blank groups. Some times this.list holds
             * a string value.
             */
            if (!(this.list && this.list.length && Array.isArray(this.list))) return
            this.list.forEach(group => {
                const key = group.name.split(' ').join('')
                if (document.getElementById(key + 'Title')) {
                    if (!(this.$refs[key][0] && this.$refs[key][0].querySelector('.container'))) {
                        document.getElementById(key + 'Title').style.display = 'none'
                    } else {
                        document.getElementById(key + 'Title').style.display = 'flex'
                    }
                }

            })
        },
        openAPIPageDialog () {
            if (this.isVideoPreview) return 
            if(!this.allowJobBuilderLauncher) return;
            let props = this.file ? { file: this.file } : null
            this.$refs.JobBuilderDialog.open(props)
        },
        truncate(str, lim) {
            return truncate(str, lim)
        },
        trackUpgradeClick () {
            localStorage.setItem("purchase_trigger", 'gpu_codec');
            localStorage.setItem("purchase_slug", this.$store.state.info.slug);
        },
    }
}
